import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Send, Phone, MapPin, CheckCircle, AlertCircle } from 'lucide-react';

const RECAPTCHA_SITE_KEY = '6LfE8ogsAAAAAJm0giJeRLvGR5uPqvZgrvB9kGcp';

const CONTACT_ENDPOINT = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api/contact`
  : '/kontakt.php';

const EXTRAS_OPTIONS = [
  { id: 'onlinegalerie', label: 'Onlinegalerie (50€)' },
  { id: 'hintergrund', label: 'Hintergrundleinwand (20€)' },
  { id: 'logo', label: 'Event-/Firmenlogo auf allen Bildern (50€)' },
  { id: 'betreuung', label: 'Betreuung der Fotobox (50€/h)' },
  { id: 'premium_accessoires', label: 'Premium Accessoires (20€)' },
];

const contactSchema = z.object({
  name: z.string().min(2, 'Bitte geben Sie Ihren Namen ein'),
  email: z.string().email('Bitte geben Sie eine gültige E-Mail-Adresse ein'),
  phone: z.string().optional(),
  date: z.string().optional(),
  event_type: z.string().optional(),
  package: z.string().optional(),
  message: z.string().optional(),
  extras: z.array(z.string()).optional(),
});

export const ContactSection = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [selectedExtras, setSelectedExtras] = useState([]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(contactSchema),
  });

  useEffect(() => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.onload = () => setRecaptchaLoaded(true);
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector(`script[src*="recaptcha"]`);
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  const getRecaptchaToken = useCallback(async () => {
    if (!recaptchaLoaded || !window.grecaptcha) {
      return '';
    }
    try {
      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'contact' });
      return token;
    } catch (error) {
      console.error('reCAPTCHA error:', error);
      return '';
    }
  }, [recaptchaLoaded]);

  const handleExtraChange = (extraId) => {
    setSelectedExtras(prev => 
      prev.includes(extraId) 
        ? prev.filter(id => id !== extraId)
        : [...prev, extraId]
    );
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const recaptchaToken = await getRecaptchaToken();
      
      const response = await fetch(CONTACT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          extras: selectedExtras,
          recaptcha_token: recaptchaToken,
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        setSubmitStatus({ 
          type: 'success', 
          message: result.message || 'Vielen Dank für Ihre Anfrage! Wir melden uns schnellstmöglich bei Ihnen.' 
        });
        reset();
        setSelectedExtras([]);
      } else {
        throw new Error(result.message || 'Ein Fehler ist aufgetreten');
      }
      
    } catch (error) {
      setSubmitStatus({ 
        type: 'error', 
        message: error.message || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 md:py-20 px-6 bg-gradient-to-b from-[#F0F9F8] to-white" data-testid="contact-section">
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left Column - Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-[#3ECDC3] font-semibold text-sm tracking-widest uppercase block mb-4">
              Jetzt anfragen
            </span>
            <h2 
              className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-6"
              data-testid="contact-headline"
            >
              Unverbindlich<br />
              <span className="text-[#3ECDC3]">anfragen</span>
            </h2>
            <p className="text-gray-600 mb-10 text-lg">
              Füllen Sie das Formular aus und wir melden uns schnellstmöglich bei Ihnen. 
              Keine versteckten Kosten, keine Überraschungen.
            </p>

            {/* Contact Info */}
            <div className="space-y-6">
              <a 
                href="tel:+4915223958867"
                className="flex items-center gap-4 text-gray-700 hover:text-[#3ECDC3] transition-colors group"
                data-testid="contact-phone"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#3ECDC3]/10 flex items-center justify-center group-hover:bg-[#3ECDC3]/20 transition-colors">
                  <Phone className="w-6 h-6 text-[#3ECDC3]" />
                </div>
                <div>
                  <span className="text-sm text-gray-500 block">Telefon</span>
                  <span className="font-semibold text-lg">+49 (0) 152 239 588 67</span>
                </div>
              </a>

              <div className="flex items-center gap-4 text-gray-700">
                <div className="w-14 h-14 rounded-2xl bg-[#3ECDC3]/10 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-[#3ECDC3]" />
                </div>
                <div>
                  <span className="text-sm text-gray-500 block">Region</span>
                  <span className="font-semibold">Bodensee, Singen, Radolfzell, Konstanz</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-3xl shadow-xl p-6 md:p-10 border border-gray-100"
          >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" data-testid="contact-form">
              {/* Name */}
              <div>
                <input
                  type="text"
                  placeholder="Ihr Name *"
                  {...register('name')}
                  className="form-input"
                  data-testid="contact-name-input"
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-2">{errors.name.message}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <input
                  type="email"
                  placeholder="Ihre E-Mail-Adresse *"
                  {...register('email')}
                  className="form-input"
                  data-testid="contact-email-input"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-2">{errors.email.message}</p>
                )}
              </div>

              {/* Phone (optional) */}
              <div>
                <input
                  type="tel"
                  placeholder="Telefonnummer (optional)"
                  {...register('phone')}
                  className="form-input"
                  data-testid="contact-phone-input"
                />
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm text-gray-500 mb-2">Gewünschtes Eventdatum</label>
                <input
                  type="date"
                  {...register('date')}
                  className="form-input w-full max-w-full box-border"
                  data-testid="contact-date-input"
                />
              </div>

              {/* Event Type */}
              <div>
                <select
                  {...register('event_type')}
                  className="form-input cursor-pointer"
                  data-testid="contact-event-select"
                >
                  <option value="">Art der Veranstaltung</option>
                  <option value="hochzeit">Hochzeit</option>
                  <option value="geburtstag">Geburtstag</option>
                  <option value="firmenevent">Firmenevent</option>
                  <option value="vereinsfest">Vereinsfest</option>
                  <option value="sonstiges">Sonstiges</option>
                </select>
              </div>

              {/* Package */}
              <div>
                <select
                  {...register('package')}
                  className="form-input cursor-pointer"
                  data-testid="contact-package-select"
                >
                  <option value="">Gewünschtes Paket</option>
                  <option value="basic">BASIC - 150€</option>
                  <option value="premium">PREMIUM - 200€</option>
                  <option value="allin">ALL IN - 300€</option>
                </select>
              </div>

              {/* Extras Checkboxes */}
              <div>
                <label className="block text-sm text-gray-500 mb-3">Optionale Extras</label>
                <div className="space-y-3">
                  {EXTRAS_OPTIONS.map((extra) => (
                    <label 
                      key={extra.id}
                      className="flex items-center gap-3 cursor-pointer group"
                    >
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={selectedExtras.includes(extra.id)}
                          onChange={() => handleExtraChange(extra.id)}
                          className="sr-only"
                        />
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                          selectedExtras.includes(extra.id)
                            ? 'bg-[#3ECDC3] border-[#3ECDC3]'
                            : 'border-gray-300 group-hover:border-[#3ECDC3]'
                        }`}>
                          {selectedExtras.includes(extra.id) && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                      <span className="text-gray-700 text-sm">{extra.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <textarea
                  placeholder="Ihre Nachricht (optional)"
                  rows={4}
                  {...register('message')}
                  className="form-input resize-none"
                  data-testid="contact-message-input"
                />
              </div>

              {/* Submit Status */}
              {submitStatus && (
                <div
                  className={`flex items-center gap-3 p-4 rounded-xl ${
                    submitStatus.type === 'success'
                      ? 'bg-green-50 border border-green-200'
                      : 'bg-red-50 border border-red-200'
                  }`}
                  data-testid="contact-status"
                >
                  {submitStatus.type === 'success' ? (
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  )}
                  <p className={submitStatus.type === 'success' ? 'text-green-700' : 'text-red-700'}>
                    {submitStatus.message}
                  </p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Wird gesendet...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Anfrage senden
                  </>
                )}
              </button>

              {/* reCAPTCHA Notice */}
              <p className="text-gray-400 text-xs text-center">
                Diese Seite ist durch reCAPTCHA geschützt. Es gelten die{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#3ECDC3] hover:underline">
                  Datenschutzbestimmungen
                </a>{' '}
                und{' '}
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-[#3ECDC3] hover:underline">
                  Nutzungsbedingungen
                </a>{' '}
                von Google.
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
